import React, { useMemo } from 'react';
import { LayoutAnimation, ScrollView } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useAvailablePractices } from 'src/hooks';
import { useUser } from 'src/providers/ClientProvider';
import { useMutation } from 'react-query';
import { MutationKeys, switchPractice, SwitchPracticeRequest } from 'src/api';
import { useAuth } from 'src/providers/AuthProvider';
import { AvailablePractice, UserConfirmationStatus } from 'src/interfaces/api/AvailablePractices';
import * as SecureStore from 'expo-secure-store';
import { IS_WEB, Margin, Size } from 'src/constants';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import styled from 'styled-components/native';
import { useNavigation } from '@react-navigation/native';
import UnconfirmedPractice from './UnconfirmedPractice';
import { X } from 'react-native-feather';
import { Body, Caption, HoverButton } from 'src/components';
import { fontStyles } from 'src/theme/globalStyles';
import { Weight } from 'src/theme';
import { StyledTitle } from '../HelpCenter/style';

const MAX_WIDTH = 446;
const MAX_HEIGHT = 500;

const SelectPractice: React.FC = () => {
  const { t } = useTranslation('selectPractice');
  const { colors } = useAppTheme();
  const { user } = useUser();
  const { logOut } = useAuth();
  const { goBack } = useNavigation();

  const { data, isFetching } = useAvailablePractices(user.email);

  const { available, currentPracticeName, unconfirmed, unregistered } = useMemo(() => {
    const available: AvailablePractice[] = [];
    const unconfirmed: AvailablePractice[] = [];
    const unregistered: AvailablePractice[] = [];
    let currentPracticeName = '';

    data?.forEach((practice) => {
      if (!practice.userRegistered) {
        unregistered.push(practice);
      } else if (practice.userConfirmationStatus === UserConfirmationStatus.PENDING) {
        unconfirmed.push(practice);
      } else {
        if (practice.practiceId === user.practiceId) {
          currentPracticeName = practice.practiceName;
        } else {
          available.push(practice);
        }
      }
    });

    return {
      available,
      currentPracticeName,
      unconfirmed,
      unregistered
    };
  }, [data, user.practiceId]);

  const { mutate, isLoading: isSwitching } = useMutation({
    mutationKey: [MutationKeys.SWITCH_PRACTICE],
    mutationFn: async (user: SwitchPracticeRequest) => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      return await switchPractice(user);
    },
    onSuccess: async (data) => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      if (!IS_WEB) {
        await SecureStore.setItemAsync('id', data.id.toString());
      }
      await logOut(data);
    }
  });

  return (
    <Container>
      <InnerView>
        <ScreenHeader>
          <BackButton onPress={() => goBack()}>
            <X width={Size.L} color={colors.onSurface} />
          </BackButton>
          <Title textAlign='center'>{t('changePracticeTitle')}</Title>
        </ScreenHeader>
        <ScrollView contentContainerStyle={{ paddingHorizontal: Size.M }}>
          <CurrentPracticeWrapper>
            <Body>{t('currentPractice')}</Body>
            <Body style={fontStyles.h3}>{currentPracticeName}</Body>
          </CurrentPracticeWrapper>

          {isFetching || isSwitching ? (
            <StyledCaption>{t('loading')}</StyledCaption>
          ) : (
            <>
              {available.map((practice) => (
                <PracticeButton
                  mode='contained'
                  key={practice.practiceId}
                  onPress={() => mutate({ userId: practice.userId })}
                >
                  <Body fontWeight={Weight.BOLD} color={colors.onPrimary}>
                    {practice.practiceName}
                  </Body>
                </PracticeButton>
              ))}

              <ContentContainer>
                {!!unconfirmed?.length && (
                  <>
                    <Divider />
                    <Body textAlign='left'>{t('associatedButUnconfirmed')}</Body>
                  </>
                )}
                {unconfirmed?.map((practice) => (
                  <UnconfirmedPractice key={practice.practiceId} practice={practice} />
                ))}
                {!!unregistered?.length && (
                  <Body textAlign='left'>{t('associatedButUnregistered')}</Body>
                )}
                {unregistered?.map((practice) => (
                  <UnconfirmedPractice
                    displayRefresh={false}
                    key={practice.practiceId}
                    practice={practice}
                  />
                ))}
              </ContentContainer>
            </>
          )}
        </ScrollView>
      </InnerView>
    </Container>
  );
};

export default SelectPractice;

const Container = styled.View`
  flex: 1;
  justify-content: ${({ theme }) => (theme.viewMode.isMobile ? 'flex-end' : 'center')};
`;

const ContentContainer = styled.View`
  gap: ${Margin.Large}px;
  margin-top: ${Margin.Large}px;
`;

const InnerView = styled.View`
  flex-shrink: 1;
  width: ${({ theme }) => (theme.viewMode.isMobile ? '100%' : `${MAX_WIDTH}px`)};
  background-color: ${({ theme }) => theme.colors.surface};
  ${({ theme }) => theme.viewMode.isMobile && 'min-height: 50%;'};
  ${({ theme }) => !theme.viewMode.isMobile && 'align-self:center;'};
  ${({ theme }) => (theme.viewMode.isMobile ? 'height: 95%;' : '')}
  border-top-left-radius: ${({ theme }) => (!theme.viewMode.isMobile ? theme.roundness : Size.M)}px;
  border-top-right-radius: ${({ theme }) =>
    !theme.viewMode.isMobile ? theme.roundness : Size.M}px;
  border-bottom-left-radius: ${({ theme }) => (!theme.viewMode.isMobile ? theme.roundness : 0)}px;
  border-bottom-right-radius: ${({ theme }) => (!theme.viewMode.isMobile ? theme.roundness : 0)}px;
  overflow: hidden;
  padding-vertical: ${Size.M}px;
  ${({ theme }) => theme.viewMode.isWeb && `max-height: ${MAX_HEIGHT}px;`}
`;

const CurrentPracticeWrapper = styled.View`
  align-items: flex-start;
  margin-bottom: ${Size.S}px;
`;

const PracticeButton = styled(HoverButton)`
  margin-top: ${Size.XS}px;
`;

const StyledCaption = styled(Caption)`
  text-align: left;
`;

const Divider = styled.View`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.disabled};
`;

const Title = styled(StyledTitle)`
  margin-bottom: 0px;
`;

const ScreenHeader = styled.View`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: ${Size.XL}px;
  padding-horizontal: ${Size.M}px;
`;

const BackButton = styled.Pressable`
  position: absolute;
  left: ${Size.M}px;
`;
